import React, { useState, useEffect } from 'react';
import { 
  Users, 
  TrendingUp, 
  DollarSign, 
  MapPin, 
  Target, 
  MousePointer2, 
  Activity,
  Briefcase,
  Zap,
  Globe,
  MessageSquare,
  PlayCircle,
  Eye,
  MousePointerClick,
  BarChart3,
  Repeat,
  ArrowUpRight,
  Info,
  CalendarDays
} from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, CartesianGrid, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from 'recharts';

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedPeriod, setSelectedPeriod] = useState('marzo'); // Toggle tra 'gennaio' e 'marzo'
  
  // --- STATI PER IL SIMULATORE ---
  const [budgetIncreasePct, setBudgetIncreasePct] = useState(0); 
  const [projectedRevenue, setProjectedRevenue] = useState(0);
  const [projectedClients, setProjectedClients] = useState(0);

  // --- DATABASE STORICO REPORT ---
  const reports = {
    gennaio: {
      label: "23/01 - Report Gennaio",
      trafficData: {
        spend: 693.56,
        impressions: 302821,
        reach: 95176,
        clicks: 6159,      
        linkClicks: 3959,  
        profileVisits: 1208, 
        landingVisits: 2367, 
        webLeads: 30,     
        extraLeads: 10,   
        totalLeads: 40,   
        cpmReach: 7.29,     
        cpcAll: 0.11,       
        cpcLink: 0.18,      
        costPerResult: 0.29,
        costPerThruPlay: 0.05,
      },
      detailTitle: "Performance Città",
      detailIcon: MapPin,
      detailData: [
        { 
          name: 'Milano (MI)', spend: 346.46, impressions: 153882, reach: 48160, visits: 1210, linkClicks: 2058, allClicks: 3149, profileVisits: 582, leads: 15, ctr: '1.34%', cpm: 7.19, cpc: 0.11, cpcLink: 0.17, costPerResult: 0.29, frequency: 3.20 
        },
        { 
          name: 'Perugia (PG)', spend: 347.10, impressions: 148939, reach: 47016, visits: 1157, linkClicks: 1901, allClicks: 3010, profileVisits: 626, leads: 15, ctr: '1.28%', cpm: 7.38, cpc: 0.12, cpcLink: 0.18, costPerResult: 0.30, frequency: 3.17 
        }
      ],
      salesData: { clients: 12, minPrice: 150, maxPrice: 400, avgPrice: 275, avgDuration: 7.5 }
    },
    marzo: {
      label: "02/04 - Report Marzo",
      trafficData: {
        spend: 816.11,      // 385.55 + 430.56
        impressions: 262059,// 29378 + 232681
        reach: 166373,      // 13706 + 152667
        clicks: 12116,      // 1342 + 10774
        linkClicks: 9954,   // 886 + 9068
        profileVisits: 8946,// Dai risultati della campagna awareness
        landingVisits: 624, // 609 + 15
        webLeads: 23,       // 23 forniti
        extraLeads: 0,      
        totalLeads: 23,     
        cpmReach: 4.90,     // (816.11 / 166373) * 1000
        cpcAll: 0.07,       // 816.11 / 12116
        cpcLink: 0.08,      // 816.11 / 9954
        costPerResult: 1.30,// 816.11 / 624
        costPerThruPlay: 0.00, // Dato rimosso / non tracciato
      },
      detailTitle: "Performance Campagne",
      detailIcon: Target,
      detailData: [
        { 
          name: 'Campagna Vendita', spend: 385.55, impressions: 29378, reach: 13706, visits: 609, linkClicks: 886, allClicks: 1342, profileVisits: 0, leads: 23, ctr: '4.57%', cpm: 13.12, cpc: 0.29, cpcLink: 0.44, costPerResult: 0.63, frequency: 2.14 
        },
        { 
          name: 'Campagna Awareness', spend: 430.56, impressions: 232681, reach: 152667, visits: 15, linkClicks: 9068, allClicks: 10774, profileVisits: 8946, leads: 0, ctr: '4.63%', cpm: 1.85, cpc: 0.04, cpcLink: 0.05, costPerResult: 28.70, frequency: 1.52 
        }
      ],
      salesData: { clients: 6, minPrice: 150, maxPrice: 400, avgPrice: 275, avgDuration: 7.5 }
    }
  };

  const currentData = reports[selectedPeriod];
  const trafficData = currentData.trafficData;
  const salesData = currentData.salesData;
  const detailData = currentData.detailData;

  // --- CALCOLI DINAMICI (si adattano al mese scelto) ---
  const globalCloseRate = (salesData.clients / trafficData.totalLeads) * 100;
  const ltv = salesData.avgPrice * salesData.avgDuration; 
  const mrr = salesData.clients * salesData.avgPrice;
  const pipelineValue = salesData.clients * ltv; 
  const pageConversionRate = ((trafficData.webLeads / trafficData.landingVisits) * 100).toFixed(2);
  const overallCTR = ((trafficData.clicks / trafficData.impressions) * 100).toFixed(1);
  const linkCTR = ((trafficData.linkClicks / trafficData.impressions) * 100).toFixed(1);

  // --- LOGICA SIMULATORE ---
  useEffect(() => {
    const multiplier = 1 + (budgetIncreasePct / 100);
    const newWebLeads = Math.round(trafficData.webLeads * multiplier);
    const totalProjectedLeads = newWebLeads + trafficData.extraLeads;
    const newClients = Math.round(totalProjectedLeads * (globalCloseRate / 100));
    const newRev = newClients * ltv;

    setProjectedRevenue(newRev);
    setProjectedClients(newClients);
  }, [budgetIncreasePct, globalCloseRate, ltv, trafficData.webLeads, trafficData.extraLeads]);

  // --- COMPONENTI UI ---
  const TabButton = ({ id, label, icon: Icon }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`flex items-center space-x-2 px-6 py-4 text-sm font-bold uppercase tracking-wider transition-all border-b-4 ${
        activeTab === id 
          ? 'border-blue-600 text-blue-900 bg-blue-50/50' 
          : 'border-transparent text-gray-400 hover:text-gray-600 hover:bg-gray-50'
      }`}
    >
      <Icon size={18} />
      <span>{label}</span>
    </button>
  );

  const BenchmarkBadge = ({ type, text }) => {
    let colorClass = "bg-gray-100 text-gray-600";
    if (type === "good") colorClass = "bg-green-100 text-green-700";
    if (type === "super") colorClass = "bg-blue-100 text-blue-700";
    if (type === "bad") colorClass = "bg-red-50 text-red-600";
    if (type === "avg") colorClass = "bg-yellow-100 text-yellow-800";
    
    return (
      <span className={`text-[10px] font-bold px-2 py-0.5 rounded ml-2 inline-flex items-center gap-1 ${colorClass}`}>
        {type === "super" && <ArrowUpRight size={10} />}
        {text}
      </span>
    );
  };

  const StatCard = ({ title, value, subtext, icon: Icon, benchmark }) => (
    <div className="p-6 border border-blue-100 rounded-xl transition-all hover:shadow-xl bg-white text-gray-800">
      <div className="flex justify-between items-start mb-4">
        <div className="p-3 rounded-lg bg-blue-50 text-blue-600">
          <Icon size={24} />
        </div>
        {benchmark && <div className="text-right">{benchmark}</div>}
      </div>
      <h3 className="text-xs font-bold uppercase tracking-widest mb-1 text-gray-500">{title}</h3>
      <div className="text-2xl md:text-3xl font-black mb-2 tracking-tight">{value}</div>
      <p className="text-xs font-medium text-blue-600">{subtext}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 font-sans selection:bg-blue-200">
      
      {/* --- HEADER --- */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4">
             <img src="https://ptsuite.it/wp-content/uploads/2024/11/logo-black.png" alt="PT Suite Logo" className="h-10 w-auto object-contain" />
            <div className="h-8 w-px bg-gray-200 hidden md:block"></div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mt-1 hidden md:block">
              Ads Reporting
            </p>
          </div>
          
          {/* Selettore Mese */}
          <div className="flex items-center gap-3 bg-gray-50 p-1.5 rounded-lg border border-gray-200">
            <CalendarDays size={18} className="text-gray-400 ml-2" />
            <select 
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="bg-transparent text-sm font-bold text-gray-800 border-none outline-none cursor-pointer py-1 pr-2 uppercase tracking-wider"
            >
              <option value="gennaio">Report Gennaio</option>
              <option value="marzo">Report Marzo (Attuale)</option>
            </select>
          </div>
        </div>
        
        {/* Navigation Tabs */}
        <div className="max-w-7xl mx-auto px-6 flex overflow-x-auto no-scrollbar gap-2">
          <TabButton id="overview" label="Visione d'Insieme" icon={Activity} />
          <TabButton id="traffic" label="Traffico & Funnel" icon={MousePointer2} />
          <TabButton id="sales" label="Vendite & Revenue" icon={DollarSign} />
          <TabButton id="simulator" label="Simulatore" icon={Target} />
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6 md:p-10 space-y-10">

        {/* --- VIEW: VISIONE D'INSIEME --- */}
        {activeTab === 'overview' && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-10">
            
            {/* Top KPI Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <StatCard 
                title="Investimento Totale" 
                value={`€${trafficData.spend.toFixed(2)}`} 
                subtext="Spesa periodo selezionato"
                icon={DollarSign}
              />
              <StatCard 
                title="Lead Totali" 
                value={trafficData.totalLeads} 
                subtext={trafficData.extraLeads > 0 ? `${trafficData.webLeads} Web + ${trafficData.extraLeads} Altri` : 'Tutti provenienti da Web'}
                icon={Users}
              />
              <StatCard 
                title="Clienti Chiusi" 
                value={salesData.clients} 
                subtext={`Tasso Chiusura Globale: ${globalCloseRate.toFixed(0)}%`}
                icon={Briefcase}
              />
              <StatCard 
                title="CPL Web Medio" 
                value={`€${(trafficData.spend/trafficData.webLeads).toFixed(2)}`} 
                subtext="Media settore: €25 - €45"
                icon={Target}
                benchmark={<BenchmarkBadge type="good" text="Ottimo" />}
              />
              <StatCard 
                title="Pipeline (Ipotetico)" 
                value={`€${(pipelineValue/1000).toFixed(1)}k`} 
                subtext="Valore LTV stimato"
                icon={TrendingUp}
              />
            </div>

            {/* Analisi Content & Video */}
            <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-blue-100 text-blue-600 rounded-lg"><PlayCircle size={24}/></div>
                  <h3 className="text-lg font-bold uppercase tracking-wider text-gray-900">Efficacia Contenuti (Video & Ads)</h3>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 mb-6">
                   <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                     <p className="text-xs font-bold text-gray-500 uppercase mb-1">Copertura Totale</p>
                     <p className="text-2xl font-black text-blue-900">{trafficData.reach.toLocaleString()}</p>
                     <p className="text-[10px] text-gray-400">Persone uniche raggiunte</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                     <div className="flex justify-between items-start">
                       <p className="text-xs font-bold text-gray-500 uppercase mb-1">CPM (Copertura)</p>
                       <BenchmarkBadge type="good" text="Media Settore" />
                     </div>
                     <p className="text-2xl font-black text-gray-900">€{trafficData.cpmReach.toFixed(2)}</p>
                     <p className="text-[10px] text-gray-400">vs Benchmark €6.00 - €9.00</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                     <div className="flex justify-between items-start">
                       <p className="text-xs font-bold text-gray-500 uppercase mb-1">Costo Visual. Pagina</p>
                       <BenchmarkBadge type={trafficData.costPerResult < 0.6 ? "super" : "avg"} text={trafficData.costPerResult < 0.6 ? "Eccellente" : "Nella Media"} />
                     </div>
                     <p className="text-2xl font-black text-blue-600">€{trafficData.costPerResult.toFixed(2)}</p>
                     <p className="text-[10px] text-gray-500 font-medium">Target media (€0.50 - €1.50)</p>
                  </div>
                   <div className="p-4 bg-gray-50 rounded-lg">
                     <div className="flex justify-between items-start">
                        <p className="text-xs font-bold text-gray-500 uppercase mb-1">CPC (Tutti)</p>
                        <BenchmarkBadge type="super" text="Super" />
                     </div>
                     <p className="text-2xl font-black text-gray-900">€{trafficData.cpcAll.toFixed(2)}</p>
                     <p className="text-[10px] text-gray-400">vs Benchmark €0.20 - €0.40</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                     <div className="flex justify-between items-start">
                        <p className="text-xs font-bold text-gray-500 uppercase mb-1">CPC (Link)</p>
                        <BenchmarkBadge type="good" text="Ottimo" />
                     </div>
                     <p className="text-2xl font-black text-gray-900">€{trafficData.cpcLink.toFixed(2)}</p>
                     <p className="text-[10px] text-gray-400">vs Benchmark €0.30 - €0.60</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg opacity-50">
                     <p className="text-xs font-bold text-gray-500 uppercase mb-1">Costo ThruPlay</p>
                     <p className="text-2xl font-black text-gray-900">{trafficData.costPerThruPlay > 0 ? `€${trafficData.costPerThruPlay.toFixed(2)}` : 'N/A'}</p>
                     <p className="text-[10px] text-gray-400">Standard di mercato</p>
                  </div>
                </div>

                <div className="bg-green-50 p-4 rounded-lg border border-green-100 flex gap-3 items-start">
                  <Zap className="text-green-600 shrink-0 mt-1" size={18} />
                  <p className="text-sm text-green-800 leading-relaxed">
                    I dati di traffico sono ottimi. Il <strong>CPC Link (€{trafficData.cpcLink.toFixed(2)})</strong> e il <strong>Costo Visualizzazione Pagina (€{trafficData.costPerResult.toFixed(2)})</strong> sono bassi, ci mostrano che la gente giusta viene attratta facilmente da quello che sta vedendo = Target perfetto e contenuti buoni, grazie anche al sistema dinamico di meta che stiamo usando che sceglie il contenuto più attrattivo in base all'utente. I dati delle performance sono ideali per il settore fitness/coaching. Questo significa che stiamo pagando meno dei competitor per la stessa visibilità. Il tasso di chiusura globale (Lead-to-Client) si mantiene solido: la media è spesso tra il 10% e il 20%.
                  </p>
                </div>
            </div>

            {/* Analisi Situazione Attuale */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 bg-white p-8 rounded-2xl border border-gray-100 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1.5 h-8 bg-blue-600 rounded-full"></div>
                  <h2 className="text-2xl font-bold text-gray-900">Situazione Attuale</h2>
                </div>
                <div className="prose prose-lg text-gray-600 leading-relaxed">
                  <p>
                    <strong>Milano e Perugia</strong> stanno performando in modo quasi identico, il che è un ottimo segnale che indica che è un buon momento, consiglio di scalare.
                  </p>
                  <p>
                    Abbiamo generato <strong>{trafficData.landingVisits.toLocaleString()} visualizzazioni della landing page</strong> a un costo medio di soli <strong>€{trafficData.costPerResult.toFixed(2)}</strong>. Questo volume di traffico ha portato a {trafficData.webLeads} lead web diretti {trafficData.extraLeads > 0 && `+ ${trafficData.extraLeads} da altre fonti`}.
                  </p>
                  <div className="bg-blue-50 p-6 rounded-xl border border-blue-100 mt-6 flex gap-4">
                    <Zap className="text-blue-600 shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-blue-900 text-sm uppercase mb-1">
                        Insight Strategico
                      </h4>
                      <p className="text-sm text-blue-800 m-0">
                        Con un CPC Link di ~€{trafficData.cpcLink.toFixed(2)}, stiamo acquisendo traffico a un costo estremamente competitivo. SCALARE!
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Breakdown Efficienza Vendita */}
              <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm flex flex-col justify-center">
                <h3 className="text-sm font-bold uppercase tracking-wider text-gray-400 mb-6 flex items-center gap-2">
                  <Target size={16} /> Efficienza di Vendita
                </h3>
                
                <div className="space-y-6">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <div className="flex justify-between items-center mb-2 pb-2 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase">
                      <span>Canale</span>
                      <span>Lead</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="flex items-center gap-2 text-sm font-medium"><Globe size={14} className="text-blue-500"/> Web Ads</span>
                      <span className="font-bold">{trafficData.webLeads}</span>
                    </div>
                    {trafficData.extraLeads > 0 && (
                      <div className="flex justify-between items-center text-gray-500">
                        <span className="flex items-center gap-2 text-sm font-medium"><MessageSquare size={14} /> Altri/Org.</span>
                        <span className="font-medium">{trafficData.extraLeads}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center mt-3 pt-2 border-t border-gray-200">
                      <span className="font-bold text-gray-900">Totale</span>
                      <span className="font-black text-lg text-blue-600">{trafficData.totalLeads}</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-2 text-sm font-bold text-gray-700">
                      <span>Conversion Rate Globale</span>
                      <BenchmarkBadge type="super" text={`${globalCloseRate.toFixed(1)}% (TOP)`} />
                    </div>
                    <div className="w-full bg-gray-100 h-3 rounded-full overflow-hidden">
                      <div className="bg-blue-600 h-full rounded-full" style={{ width: `${Math.min(globalCloseRate, 100)}%` }}></div>
                    </div>
                    <p className="text-xs text-gray-400 mt-2 text-center">Media di mercato: 10-20% per traffico freddo</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* --- VIEW: TRAFFICO (Funnel Leggibile) --- */}
        {activeTab === 'traffic' && (
          <div className="animate-in fade-in duration-500 grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Funnel Visual */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 p-8 shadow-sm">
              <h3 className="text-2xl font-bold mb-10 flex items-center gap-3 text-gray-900">
                <div className="p-2 bg-blue-100 text-blue-600 rounded-lg"><MousePointer2 size={24}/></div>
                Funnel di Acquisizione Web
              </h3>
              
              <div className="space-y-10 relative pl-4">
                <div className="absolute left-[35px] top-4 bottom-10 w-1 bg-gray-100 rounded-full"></div>

                {/* Step 1: Impression */}
                <div className="relative z-10 flex items-center gap-6">
                  <div className="w-14 h-14 rounded-full bg-gray-200 border-4 border-white shadow-sm flex items-center justify-center text-gray-600 font-bold text-lg shrink-0 z-20">1</div>
                  <div className="flex-1">
                    <div className="flex justify-between items-end mb-2">
                      <span className="text-sm font-bold text-gray-600 uppercase tracking-widest">Visibilità (Impression)</span>
                      <span className="text-3xl font-black text-gray-800">{trafficData.impressions > 1000 ? (trafficData.impressions/1000).toFixed(1) + 'k' : trafficData.impressions}</span>
                    </div>
                    <div className="w-full bg-gray-200 h-4 rounded-full">
                      <div className="bg-gray-400 h-full rounded-full opacity-60 w-full"></div>
                    </div>
                    {/* MODIFICA RICHIESTA: FONT INGRANDITI PER COPERTURA */}
                    <div className="mt-3 bg-gray-50 p-3 rounded-lg border border-gray-200 inline-block w-full">
                       <p className="text-xl font-black text-blue-900">Copertura: {trafficData.reach.toLocaleString()} <span className="text-lg font-medium text-gray-600">utenti unici raggiunti</span></p>
                    </div>
                  </div>
                </div>

                {/* Step 2: Clicks SPLIT */}
                <div className="relative z-10 flex items-start gap-6">
                  <div className="w-14 h-14 rounded-full bg-blue-100 border-4 border-white shadow-sm flex items-center justify-center text-blue-600 font-bold text-lg shrink-0 z-20 mt-2">2</div>
                  <div className="flex-1">
                    <div className="flex justify-between items-end mb-2">
                      <div>
                         <span className="text-sm font-bold text-blue-800 uppercase tracking-widest">Interesse (Click Totali)</span>
                         <span className="text-xs text-blue-500 ml-2 font-bold bg-blue-50 px-2 py-0.5 rounded">CTR Complessivo: ~{overallCTR}%</span>
                      </div>
                      <span className="text-3xl font-black text-blue-800">{trafficData.clicks.toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-blue-50 h-4 rounded-full overflow-hidden mb-4 border border-blue-100">
                      <div className="bg-blue-400 h-full rounded-full w-[70%]"></div>
                    </div>
                    
                    {/* Split dettaglio click */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                        <div className="flex items-center gap-2 mb-1">
                          <MousePointerClick size={14} className="text-blue-600"/>
                          <span className="text-xs font-bold text-blue-800 uppercase">Click sul Link</span>
                        </div>
                        <p className="text-xl font-black text-blue-600">{trafficData.linkClicks.toLocaleString()}</p>
                        <p className="text-[10px] text-blue-500 font-medium">CTR Link ~{linkCTR}% (Media 0.9%)</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-2 mb-1">
                          <Eye size={14} className="text-gray-600"/>
                          <span className="text-xs font-bold text-gray-600 uppercase">Visite Profilo</span>
                        </div>
                        <p className="text-xl font-black text-gray-700">{trafficData.profileVisits.toLocaleString()}</p>
                        <p className="text-[10px] text-gray-400">Engagement Diretto</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 3: Landing Visits */}
                <div className="relative z-10 flex items-center gap-6">
                  <div className="w-14 h-14 rounded-full bg-blue-100 border-4 border-white shadow-sm flex items-center justify-center text-blue-600 font-bold text-lg shrink-0 z-20">3</div>
                  <div className="flex-1">
                    <div className="flex justify-between items-end mb-2">
                      <span className="text-base font-bold text-blue-900 uppercase tracking-widest">Risultati (Visual. Pagina)</span>
                      <span className="text-4xl font-black text-blue-600">{trafficData.landingVisits.toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-gray-50 h-6 rounded-full overflow-hidden shadow-inner">
                      <div className="bg-blue-500 h-full rounded-full w-[60%] shadow-lg"></div>
                    </div>
                    <p className="text-sm text-gray-400 mt-2">Costo medio per risultato: €{trafficData.costPerResult.toFixed(2)}</p>
                  </div>
                </div>

                {/* Step 4: Lead */}
                <div className="relative z-10 flex items-center gap-6">
                  <div className="w-14 h-14 rounded-full bg-green-100 border-4 border-white shadow-sm flex items-center justify-center text-green-600 font-bold text-lg shrink-0 z-20">4</div>
                  <div className="flex-1">
                    <div className="flex justify-between items-end mb-2">
                      <div>
                        <span className="text-base font-bold text-green-800 uppercase tracking-widest">Lead Web</span>
                        <span className="text-xs text-orange-600 ml-2 font-bold bg-orange-50 px-2 py-0.5 rounded border border-orange-100">CR Pagina: {pageConversionRate}%</span>
                      </div>
                      <span className="text-5xl font-black text-green-600">{trafficData.webLeads}</span>
                    </div>
                    <div className="w-full bg-gray-50 h-6 rounded-full overflow-hidden shadow-inner mb-2">
                      <div className="bg-green-500 h-full rounded-full w-[10%] shadow-lg"></div>
                    </div>
                    {/* MODIFICA RICHIESTA: FONT INGRANDITI PER NOTA CR */}
                    <div className="flex items-start gap-3 text-base font-medium text-gray-800 italic bg-yellow-50 p-5 rounded-lg mt-4 border border-yellow-200 shadow-sm">
                       <Info size={24} className="mt-0.5 shrink-0 text-yellow-600"/>
                       <span>
                          <strong>Nota CR Pagina ({pageConversionRate}%):</strong> {pageConversionRate < 2 ? "Il tasso di conversione è migliorabile. Target settore: 2-3%. Ottimizzare la landing potrebbe raddoppiare i lead a parità di spesa." : "Il tasso di conversione è solido e sopra la soglia critica. La pagina sta performando molto bene rispetto al target del 2-3%."}
                       </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Split Dettagli (Città o Campagne in base al mese) */}
            <div className="bg-white rounded-2xl border border-gray-100 p-8 shadow-sm h-fit">
              <h3 className="text-lg font-bold mb-6 flex items-center gap-2 text-gray-900">
                {currentData.detailIcon && <currentData.detailIcon size={20} className="text-blue-500" />}
                {currentData.detailTitle}
              </h3>
              <div className="space-y-4">
                {detailData.map((item, idx) => (
                  <div key={idx} className="bg-gray-50 p-5 rounded-xl border border-gray-100 hover:border-blue-200 transition-colors">
                    <div className="flex justify-between items-center mb-4">
                      <div>
                         <h4 className="font-bold text-xl text-gray-900">{item.name}</h4>
                         <p className="text-[10px] text-gray-400 uppercase mt-1">Spesa: €{item.spend.toFixed(2)}</p>
                      </div>
                      <div className="text-right">
                         <div className="flex items-center justify-end gap-1 mb-1">
                            <span className="text-xs font-bold bg-white border border-gray-200 text-gray-600 px-3 py-1 rounded-full">CTR {item.ctr}</span>
                            <span className="text-[9px] text-green-600 font-bold bg-green-50 px-1 rounded border border-green-100">Buono</span>
                         </div>
                         <span className="text-[10px] text-gray-400">CPM €{item.cpm.toFixed(2)}</span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-y-4 gap-x-2 border-t border-gray-200 pt-4">
                      {/* Metriche Dettaglio */}
                      <div>
                        <p className="text-[10px] uppercase text-gray-400 font-bold">Impression</p>
                        <p className="font-bold text-lg text-gray-900">{item.impressions.toLocaleString()}</p>
                      </div>

                      <div className="text-right">
                         <p className="text-[10px] uppercase text-gray-400 font-bold">Frequenza</p>
                         <div className="flex items-center justify-end gap-1">
                           <p className="font-black text-lg text-orange-600">{item.frequency.toFixed(2)}</p>
                           <Repeat size={12} className="text-orange-600" />
                         </div>
                         {item.frequency >= 3 && <p className="text-[9px] text-orange-500 font-medium">Attenzione saturazione</p>}
                      </div>
                      
                      <div>
                        <p className="text-[10px] uppercase text-gray-400 font-bold">Risultati (Visite)</p>
                        <p className="font-bold text-lg text-blue-900">{item.visits.toLocaleString()}</p>
                        <p className="text-[9px] text-gray-400">Costo: €{item.costPerResult.toFixed(2)}</p>
                      </div>
                      
                      <div className="text-right">
                         <p className="text-[10px] uppercase text-gray-400 font-bold">Clic Link</p>
                         <p className="font-bold text-base text-blue-600">{item.linkClicks.toLocaleString()}</p>
                         <p className="text-[9px] text-gray-400">CPC: €{item.cpcLink.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-sm text-gray-500 leading-relaxed italic border-l-2 border-blue-200 pl-3">
                {selectedPeriod === 'gennaio' 
                  ? '"CTR di entrambe le città sopra la media (1.0%). Ottima risposta creativa."'
                  : '"La campagna Vendita converte perfettamente a costo basso, la Awareness esplode sui numeri di profilo."'}
              </p>
            </div>
          </div>
        )}

        {/* --- VIEW: VENDITE --- */}
        {activeTab === 'sales' && (
          <div className="animate-in fade-in duration-500 space-y-8">
            <div className="bg-blue-900 text-white p-8 md:p-12 rounded-3xl relative overflow-hidden shadow-xl">
              <div className="relative z-10">
                <h2 className="text-3xl font-bold mb-2">Performance Economica</h2>
                <p className="text-blue-200 max-w-2xl text-lg">
                  Analisi basata sui {salesData.clients} clienti acquisiti nel periodo selezionato, con spesa media €150-€400.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12 pt-8 border-t border-blue-800">
                  <div>
                    <p className="text-xs text-blue-300 uppercase tracking-widest mb-1">Fatturato Mensile (MRR)</p>
                    <p className="text-5xl font-bold">€{mrr.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-blue-300 uppercase tracking-widest mb-1">Pipeline Ipotetica</p>
                    <p className="text-5xl font-bold">€{pipelineValue.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-blue-300 uppercase tracking-widest mb-1">LTV Medio Cliente</p>
                    <p className="text-5xl font-bold text-green-400">€{ltv.toLocaleString()}</p>
                  </div>
                </div>
              </div>
              <div className="absolute top-0 right-0 w-80 h-80 bg-blue-600 rounded-full opacity-20 -mr-20 -mt-20 blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500 rounded-full opacity-10 -ml-10 -mb-10 blur-2xl"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-sm">
                <h3 className="font-bold text-xl mb-6 text-gray-900">Composizione Prezzi</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-5 rounded-xl bg-gray-50 border border-gray-100">
                    <span className="font-medium text-gray-600">Servizio Entry</span>
                    <span className="font-bold text-gray-900">€150</span>
                  </div>
                  <div className="flex items-center justify-between p-5 rounded-xl bg-blue-600 text-white shadow-lg transform scale-105">
                    <span className="font-bold">Media Reale</span>
                    <span className="font-black text-xl">€275</span>
                  </div>
                  <div className="flex items-center justify-between p-5 rounded-xl bg-gray-50 border border-gray-100">
                    <span className="font-medium text-gray-600">Servizio Premium</span>
                    <span className="font-bold text-gray-900">€400</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-sm flex flex-col justify-center">
                <h3 className="font-bold text-xl mb-2 text-gray-900">Durata Percorsi</h3>
                <p className="text-gray-500 mb-8">La stabilità del cashflow è garantita da contratti fino a 12 mesi.</p>
                <div className="flex items-end gap-4 h-40 px-4">
                   <div className="bg-gray-200 w-1/3 h-[30%] rounded-t-lg relative group flex items-end justify-center pb-2">
                     <span className="text-xs font-bold text-gray-500">3 Mesi</span>
                   </div>
                   <div className="bg-blue-600 w-1/3 h-[65%] rounded-t-lg relative group flex items-end justify-center pb-2 shadow-lg shadow-blue-200">
                     <span className="text-lg font-bold text-white mb-8">7.5</span>
                     <span className="text-xs font-medium text-blue-200 absolute bottom-2">Media</span>
                   </div>
                   <div className="bg-gray-200 w-1/3 h-full rounded-t-lg relative group flex items-end justify-center pb-2">
                     <span className="text-xs font-bold text-gray-500">12 Mesi</span>
                   </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* --- VIEW: SIMULATORE --- */}
        {activeTab === 'simulator' && (
          <div className="animate-in fade-in duration-500">
            <div className="bg-white rounded-3xl border border-gray-200 shadow-xl overflow-hidden">
              <div className="p-8 md:p-12 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
                 <h2 className="text-3xl font-bold mb-4">Simulatore di Scalabilità</h2>
                 <p className="text-blue-100 max-w-xl text-lg">
                  Quanto potremmo crescere aumentando il budget, mantenendo le performance attuali? Stima.
                </p>

                <div className="mt-12">
                  <div className="flex justify-between items-end mb-4">
                    <label className="text-sm font-bold uppercase tracking-wider text-blue-200">Aumento Budget</label>
                    <div className="text-right">
                       <span className="text-5xl font-black block">+{budgetIncreasePct}%</span>
                       <span className="text-lg font-medium text-blue-200 bg-white/10 px-3 py-1 rounded mt-2 inline-block">
                         + €{(trafficData.spend * (budgetIncreasePct/100)).toFixed(0)} investiti
                       </span>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="200"
                    step="10"
                    value={budgetIncreasePct}
                    onChange={(e) => setBudgetIncreasePct(parseInt(e.target.value))}
                    className="w-full h-4 bg-blue-900/30 rounded-full appearance-none cursor-pointer accent-white"
                  />
                  <div className="flex justify-between mt-3 text-xs font-medium text-blue-200">
                    <span>Attuale (€{trafficData.spend.toFixed(0)})</span>
                    <span>Raddoppio (+100%)</span>
                    <span>Triplicazione (+200%)</span>
                  </div>
                </div>
              </div>

              <div className="p-8 md:p-12 bg-white">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                  <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
                    <h4 className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-2">Lead Totali Stimati</h4>
                    <div className="text-4xl font-bold text-gray-900">
                      {Math.round(trafficData.webLeads * (1 + budgetIncreasePct/100)) + trafficData.extraLeads}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-blue-600 font-bold mt-2">
                      <TrendingUp size={16} />
                      +{Math.round(trafficData.webLeads * (budgetIncreasePct/100))} extra web
                    </div>
                  </div>
                  
                  <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
                    <h4 className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-2">Clienti Proiettati</h4>
                    <div className="text-4xl font-bold text-gray-900">{projectedClients}</div>
                    <p className="text-xs text-gray-500 mt-2">Assumendo CR stabile al {globalCloseRate.toFixed(0)}%</p>
                  </div>
                  
                  <div className="p-6 bg-blue-50 rounded-2xl border border-blue-100">
                    <h4 className="text-blue-400 text-xs font-bold uppercase tracking-wider mb-2">Nuova Pipeline Ipotetica</h4>
                    <div className="text-4xl font-bold text-blue-700">€{projectedRevenue.toLocaleString()}</div>
                    <div className="flex items-center gap-2 text-sm text-green-600 font-bold mt-2">
                       <Zap size={16} fill="currentColor" />
                       +€{(projectedRevenue - pipelineValue).toLocaleString()} crescita
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
};

export default Dashboard;